import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  CustomerContact,
  CustomerContactCreate,
  CustomerContactList,
  CustomerContactUpdate,
} from '@/types/api';

/**
 * CRUD-Hooks für Kundenkontakte (InvoiceNinja + manuell).
 *
 * Mutations invalidieren Kontakte, die effektive Empfängerliste und das
 * Kunden-Detail: die Übersicht zeigt `notification_emails`, und der
 * Recipients-GET ist nur noch die Read-Sicht auf dieselben Kontakte.
 */

function invalidateContactDependentQueries(
  qc: ReturnType<typeof useQueryClient>,
  customerId: string
): void {
  void qc.invalidateQueries({ queryKey: queryKeys.customers.contacts(customerId) });
  void qc.invalidateQueries({
    queryKey: queryKeys.customers.notificationRecipients(customerId),
  });
  // Schalter an einem Kontakt ändern die wirksame Empfängerliste.
  void qc.invalidateQueries({
    queryKey: queryKeys.customers.effectiveRecipients(customerId),
  });
  void qc.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
}

export function useCustomerContacts(
  customerId: string | undefined
): ReturnType<typeof useQuery<CustomerContactList>> {
  return useQuery<CustomerContactList>({
    queryKey: queryKeys.customers.contacts(customerId ?? ''),
    queryFn: () => api.get<CustomerContactList>(`/customers/${customerId ?? ''}/contacts`),
    enabled: Boolean(customerId),
  });
}

export interface CreateCustomerContactInput {
  customerId: string;
  body: CustomerContactCreate;
}

export function useCreateCustomerContact(): ReturnType<
  typeof useMutation<CustomerContact, Error, CreateCustomerContactInput>
> {
  const qc = useQueryClient();
  return useMutation<CustomerContact, Error, CreateCustomerContactInput>({
    mutationFn: ({ customerId, body }) =>
      api.post<CustomerContact, CustomerContactCreate>(`/customers/${customerId}/contacts`, body),
    onSuccess: (_data, { customerId }) => {
      invalidateContactDependentQueries(qc, customerId);
    },
  });
}

export interface UpdateCustomerContactInput {
  customerId: string;
  contactId: string;
  body: CustomerContactUpdate;
}

export function useUpdateCustomerContact(): ReturnType<
  typeof useMutation<CustomerContact, Error, UpdateCustomerContactInput>
> {
  const qc = useQueryClient();
  return useMutation<CustomerContact, Error, UpdateCustomerContactInput>({
    mutationFn: ({ customerId, contactId, body }) =>
      api.patch<CustomerContact, CustomerContactUpdate>(
        `/customers/${customerId}/contacts/${contactId}`,
        body
      ),
    onSuccess: (_data, { customerId }) => {
      invalidateContactDependentQueries(qc, customerId);
    },
  });
}

export interface DeleteCustomerContactInput {
  customerId: string;
  contactId: string;
}

export function useDeleteCustomerContact(): ReturnType<
  typeof useMutation<void, Error, DeleteCustomerContactInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteCustomerContactInput>({
    mutationFn: ({ customerId, contactId }) =>
      api.delete(`/customers/${customerId}/contacts/${contactId}`),
    onSuccess: (_data, { customerId }) => {
      invalidateContactDependentQueries(qc, customerId);
    },
  });
}
